<template>
  <section>
    <h1>Admin Dashboard</h1>
    <hr />

    <!-- Scheduler Status -->
    <div class="mb-3">
      <h3>
        Scheduler Status:
        <span :class="statusClass">{{ schedulerStatus.status }}</span>
      </h3>
      <button
        v-if="schedulerStatus.status === 'running'"
        class="btn btn-warning"
        @click="pauseScheduler"
      >
        Pause Scheduler
      </button>
      <button
        v-if="schedulerStatus.status === 'paused'"
        class="btn btn-success"
        @click="resumeScheduler"
      >
        Resume Scheduler
      </button>
    </div>

    <!-- Jobs In Progress -->
    <div class="mb-3">
      <h4>Jobs in Progress: {{ jobsInProgressCount }}</h4>
    </div>

    <!-- Recent Failures Table -->
    <div class="mb-3">
      <h4>Recent Job Failures</h4>
      <table class="table table-striped">
        <thead class="table-dark">
          <tr>
            <th>Date</th>
            <th>Job ID</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in recentFailures" :key="job.id">
            <td>{{ formatDate(job.Failure_Details.time) }}</td>
            <td>
              <router-link :to="{ name: 'job', params: { id: job.id } }">{{
                job.id
              }}</router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Job Completion Stats -->
    <div class="mb-3">
      <h4>Job Completion Statistics</h4>
      <table class="table table-striped">
        <thead class="table-dark">
          <tr>
            <th>Period</th>
            <th>Jobs Completed</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Today</td>
            <td>{{ jobStats.today || 0 }}</td>
            <!-- Access jobStats directly -->
          </tr>
          <tr>
            <td>Last 7 Days</td>
            <td>{{ jobStats.last7Days || 0 }}</td>
            <!-- Access jobStats directly -->
          </tr>
          <tr>
            <td>Last 30 Days</td>
            <td>{{ jobStats.last30Days || 0 }}</td>
            <!-- Access jobStats directly -->
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import $http from "@/http";

export default {
  name: "AdminView",

  // Fetch data on creation
  created() {
    this.fetchData();
    this.interval = setInterval(this.fetchData, 10000); // Auto-update every 10 seconds
  },

  // Cleanup when component is destroyed
  beforeUnmount() {
    clearInterval(this.interval);
  },

  // Watcher to observe changes in jobStats
  watch: {
    jobStats: {
      deep: true, // Watch deeply for changes inside the jobStats object
      handler(newVal) {
        console.log("Job stats updated:", newVal); // Debugging: see the new jobStats when updated
      },
    },
  },

  computed: {
    ...mapGetters({
      schedulerStatus: "stateSchedulerStatus", // Mapping scheduler status from Vuex
      jobsInProgress: "stateJobsInProgress", // Mapping jobs in progress from Vuex
      recentFailures: "stateRecentFailures", // Mapping recent job failures from Vuex
      jobStats: "stateJobStats", // Map the jobStats getter here
    }),
  },
  methods: {
    ...mapActions([
      "getJobsInProgress",
      "getSchedulerStatus",
      "getRecentFailures",
      "getJobCompletionStats",
    ]), // Vuex actions

    async fetchData() {
      try {
        await this.getSchedulerStatus(); // Fetch scheduler status
        await this.getJobsInProgress(); // Fetch jobs in progress
        await this.getRecentFailures(); // Fetch recent job failures

        // Fetch job completion stats and log them
        const stats = await this.getJobCompletionStats(); // Fetch job completion stats
        console.log("Job completion stats:", stats); // Log the fetched stats

        // Optional: Force UI update after fetch completes
        this.$nextTick(() => {
          console.log("UI updated after fetching data");
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },

    async pauseScheduler() {
      try {
        await $http.get("/admin/pause_processing");
      } catch (error) {
        console.error("Failed to pause scheduler:", error);
      } finally {
        this.getSchedulerStatus();
      }
    },

    async resumeScheduler() {
      try {
        await $http.get("/admin/resume_processing");
      } catch (error) {
        console.error("Failed to resume scheduler:", error);
      } finally {
        this.getSchedulerStatus();
      }
    },

    formatDate(date) {
      return moment(date).format("M/D/YY h:mm A");
    },

    statusClass() {
      return this.schedulerStatus.status === "running"
        ? "badge bg-success"
        : "badge bg-danger";
    },
  },
};
</script>

<style scoped>
.badge {
  font-size: 1.2em;
}
</style>
